/**
 * Permission checker diagnostic component
 * Helps debug permission issues
 */

import { useState } from 'react';
import { auth } from '../../services/firebase';

export default function PermissionChecker() {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<any>(null);

  const checkPermissions = async () => {
    setChecking(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setResult({ error: 'Not logged in' });
        return;
      }

      // Get the ID token with claims
      const tokenResult = await user.getIdTokenResult(true); // Force refresh

      setResult({
        email: user.email,
        uid: user.uid,
        customClaims: tokenResult.claims,
        roles: tokenResult.claims.roles || [],
        permissions: tokenResult.claims.permissions || [],
      });
    } catch (error: any) {
      setResult({ error: error.message });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
      <h3 className="text-lg font-bold text-yellow-900 dark:text-yellow-200 mb-2">
        🔍 Permission Diagnostic Tool
      </h3>
      <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-4">
        If you're having issues creating products, use this tool to check your current permissions.
      </p>

      <button
        onClick={checkPermissions}
        disabled={checking}
        className="btn bg-yellow-600 hover:bg-yellow-700 text-white mb-4"
      >
        {checking ? 'Checking...' : 'Check My Permissions'}
      </button>

      {result && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          {result.error ? (
            <div className="text-red-600 dark:text-red-400">
              <strong>Error:</strong> {result.error}
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div>
                <strong className="text-gray-700 dark:text-gray-300">Email:</strong>{' '}
                <span className="text-gray-900 dark:text-white">{result.email}</span>
              </div>
              <div>
                <strong className="text-gray-700 dark:text-gray-300">User ID:</strong>{' '}
                <span className="text-gray-900 dark:text-white font-mono text-xs">{result.uid}</span>
              </div>
              <div>
                <strong className="text-gray-700 dark:text-gray-300">Roles:</strong>{' '}
                {result.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {result.roles.map((role: string) => (
                      <span
                        key={role}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded text-xs"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-red-600 dark:text-red-400">No roles assigned ⚠️</span>
                )}
              </div>
              <div>
                <strong className="text-gray-700 dark:text-gray-300">Permissions:</strong>{' '}
                {result.permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {result.permissions.map((perm: string) => (
                      <span
                        key={perm}
                        className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded text-xs font-mono"
                      >
                        {perm}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-red-600 dark:text-red-400">No permissions ⚠️</span>
                )}
              </div>

              {result.permissions.length === 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 mt-4">
                  <strong className="text-red-900 dark:text-red-200">⚠️ Issue Found:</strong>
                  <p className="text-red-800 dark:text-red-300 text-sm mt-1">
                    You don't have any permissions assigned. To create products, you need the "administrator" role or a role with "product:*" permission.
                  </p>
                  <p className="text-red-800 dark:text-red-300 text-sm mt-2">
                    <strong>Solution:</strong> Ask an administrator to assign you the "administrator" role via the Users &gt; Roles tab.
                  </p>
                </div>
              )}

              {result.permissions.length > 0 &&
                !result.permissions.includes('product:*') &&
                !result.permissions.includes('product:create') && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded p-3 mt-4">
                    <strong className="text-orange-900 dark:text-orange-200">⚠️ Issue Found:</strong>
                    <p className="text-orange-800 dark:text-orange-300 text-sm mt-1">
                      You have permissions, but not the ones needed to create products. You need either "product:*" or "product:create".
                    </p>
                  </div>
                )}

              {(result.permissions.includes('product:*') || result.permissions.includes('product:create')) && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-3 mt-4">
                  <strong className="text-green-900 dark:text-green-200">✅ Permissions Look Good!</strong>
                  <p className="text-green-800 dark:text-green-300 text-sm mt-1">
                    You have the necessary permissions to create products. If you're still experiencing issues, try:
                  </p>
                  <ul className="list-disc list-inside text-green-800 dark:text-green-300 text-sm mt-2 space-y-1">
                    <li>Signing out and signing back in to refresh your token</li>
                    <li>Checking the browser console for detailed error messages</li>
                    <li>Verifying all required fields are filled in the product form</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
